/**
 * Arranque do escritório.
 *
 * Ordem que importa: o CSS do design system primeiro (senão a primeira pintura
 * sai sem estilo), `icons.js` antes de `macos.js` (o segundo procura o primeiro
 * ao iniciar), e só então o aplicativo.
 */

import '@pcp/ui/estilo/pcp.css';
import '../../../vendor/macos/js/icons.js';
import '../../../vendor/macos/js/macos.js';

import { botao, criarRoteador, h, hidratar, notificar, render, sf, telaDeAbertura } from '@pcp/ui';
import { criarAplicacao, type Aplicacao } from './estado.js';
import { montarCasca, NAVEGACAO } from './casca.js';
import { telaCarteira } from './telas/carteira.js';
import { telaDetalhe } from './telas/detalhe.js';
import { telaQuadro, acoesDoQuadro } from './telas/quadro.js';
import { telaPainel } from './telas/painel.js';
import { telaExpedicao } from './telas/expedicao.js';
import { carregarExemplos, telaImportacoes } from './telas/importacoes.js';
import { telaTriagem } from './telas/triagem.js';
import { telaAlertas } from './telas/alertas.js';
import { telaRelatorios } from './telas/relatorios.js';
import { telaAparelhos, telaFabrica, telaParametros, telaPessoas } from './telas/cadastros.js';
import { avisarEmFolha } from './dialogos.js';
import { telaDeEntrada } from './entrada.js';

async function iniciar(): Promise<void> {
  const raiz = document.getElementById('raiz');
  if (!raiz) throw new Error('Elemento #raiz não encontrado.');

  render(raiz, telaDeAbertura({ descricao: 'Abrindo o PCP…' }));

  const roteador = criarRoteador('/carteira');
  const base = await criarAplicacao(roteador);

  let desenhando = false;
  const app: Aplicacao = {
    ...base,
    redesenhar: () => {
      if (desenhando) return;
      desenhando = true;
      queueMicrotask(() => {
        desenhando = false;
        void desenhar();
      });
    },
  };

  /* ⚠️ O SISTEMA É 100% ONLINE. A tela de entrada é a PORTA, não uma opção:
     sem sessão o aplicativo não abre. Não existe mais "usar só neste
     computador" — o que aparecia antes como um atalho, aqui seria um sistema
     paralelo, com dados que ninguém mais vê.
     MAS: com sessão salva (o `Nuvem.iniciar()` já restaurou), o F5 não pede
     login de novo — é o que faz o aparelho voltar logado depois de reiniciar. */
  if (!app.nuvem.logado) {
    await new Promise<void>((resolve) => {
      telaDeEntrada(app, raiz, () => resolve());
    });
  }

  const casca = montarCasca(app);
  render(raiz, casca.elemento);

  // Com sessão, o sincronismo roda sozinho: ao voltar a rede e a cada 30 s.
  app.nuvem.ligarAutomatico();

  async function desenhar(): Promise<void> {
    const rota = roteador.atual();
    const item = NAVEGACAO.find((i) => i.rota === rota.nome);
    const modoTv = rota.nome === 'quadro' && rota.parametros['id'] === 'tv';

    const [alertas, triagem, importacoes, carteira, dispositivos] = await Promise.all([
      app.repo.alertas(),
      app.repo.triagem(),
      app.repo.importacoes(),
      app.repo.carteira(),
      app.repo.dispositivos(),
    ]);

    const contagens = {
      alertas: alertas.length,
      triagem: triagem.length,
      quarentena: importacoes.filter((i) => i.status === 'quarentena').length,
      atrasadas: carteira.filter((l) => l.atrasada).length,
      tablets: dispositivos.filter((d) => d.ativo).length,
    };

    const titulo =
      rota.nome === 'os'
        ? `OS ${rota.parametros['id'] ?? ''}`
        : (item?.rotulo ?? 'PCP');

    // O modo TV esconde a casca: o quadro na parede é só o quadro.
    const topo = casca.elemento.querySelector<HTMLElement>('.topo');
    const rail = casca.elemento.querySelector<HTMLElement>('.rail');
    if (topo) topo.hidden = modoTv;
    if (rail) rail.hidden = modoTv;
    casca.elemento.classList.toggle('pcp-tv', modoTv);

    casca.atualizar(rota.nome, titulo, contagens, alertas);
    casca.definirAcoes(acoesDaRota(app, rota.nome, modoTv));

    switch (rota.nome) {
      case 'carteira':
        await telaCarteira(app, casca.conteudo);
        break;
      case 'os':
        await telaDetalhe(app, casca.conteudo, Number(rota.parametros['id'] ?? 0));
        break;
      case 'quadro':
        await telaQuadro(app, casca.conteudo, modoTv);
        break;
      case 'painel':
        await telaPainel(app, casca.conteudo);
        break;
      case 'expedicao':
        await telaExpedicao(app, casca.conteudo);
        break;
      case 'importacoes':
        await telaImportacoes(app, casca.conteudo);
        break;
      case 'triagem':
        await telaTriagem(app, casca.conteudo);
        break;
      case 'alertas':
        await telaAlertas(app, casca.conteudo);
        break;
      case 'relatorios':
        await telaRelatorios(app, casca.conteudo);
        break;
      case 'fabrica':
        await telaFabrica(app, casca.conteudo);
        break;
      case 'pessoas':
        await telaPessoas(app, casca.conteudo);
        break;
      case 'aparelhos':
        await telaAparelhos(app, casca.conteudo);
        break;
      case 'parametros':
        await telaParametros(app, casca.conteudo);
        break;
      default:
        roteador.substituir('/carteira');
        return;
    }

    hidratar(casca.conteudo);
  }

  roteador.aoMudar(() => void desenhar());
  app.repo.aoMudar(() => app.redesenhar());
  roteador.iniciar();

  ligarAtalhos(app);
  ligarEventosDaBarraDeMenus(app);

  // `?exemplo=1` abre o aplicativo já com as 21 OS de amostra dentro. É como
  // se mostra o sistema a quem nunca viu, e como uma versão publicada é
  // conferida sem depender de arquivo na mão de ninguém.
  if (new URLSearchParams(location.search).has('exemplo')) {
    if ((await app.repo.carteira()).length === 0) await carregarExemplos(app, true);
  }

  // A varredura roda fora do caminho de desenho: no arranque e no relógio.
  // Chamá-la de dentro de uma tela criaria um laço — ela escreve, escrever
  // redesenha, redesenhar varreria de novo.
  void app.repo.varrerAlertas();
  setInterval(() => void app.repo.varrerAlertas(), 5 * 60_000);
}

function acoesDaRota(app: Aplicacao, rota: string, modoTv: boolean) {
  if (rota === 'quadro') return acoesDoQuadro(app, modoTv);
  if (rota === 'carteira' || rota === 'painel') {
    return [
      h(
        'button.bt.bt-s.bt-p2',
        { type: 'button', onclick: () => app.roteador.ir('/importacoes') },
        [sf('tray'), 'Importar OS'],
      ),
    ];
  }
  return null;
}

function ligarAtalhos(app: Aplicacao): void {
  document.addEventListener('keydown', (ev) => {
    const alvo = ev.target as HTMLElement | null;
    const digitando =
      alvo && (alvo.tagName === 'INPUT' || alvo.tagName === 'TEXTAREA' || alvo.isContentEditable);

    if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'i') {
      ev.preventDefault();
      app.roteador.ir('/importacoes');
      return;
    }
    if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'f') {
      ev.preventDefault();
      document.querySelector<HTMLInputElement>('.busca input')?.focus();
      return;
    }
    if (digitando) return;

    // Números 1–4 pulam entre as telas de produção, como as abas de um app.
    const atalhos: Record<string, string> = { '1': '/carteira', '2': '/quadro', '3': '/painel', '4': '/alertas' };
    const destino = atalhos[ev.key];
    if (destino) app.roteador.ir(destino);
  });
}

function ligarEventosDaBarraDeMenus(app: Aplicacao): void {
  document.addEventListener('pcp:exportar', () => {
    void exportarCarteira(app);
  });

  document.addEventListener('pcp:ajuda', (ev) => {
    const qual = (ev as CustomEvent).detail as string;
    if (qual === 'ajuda-importacao') {
      void avisarEmFolha(
        'Como a OS entra no sistema',
        h('.vstack.gap-2', [
          h('p', { style: { margin: 0 } },
            'O sistema atual da gráfica já salva o PDF de cada OS. Um agente instalado naquele computador vigia a pasta e envia o arquivo para cá — sem ninguém digitar nada.'),
          h('p', { style: { margin: 0 } },
            'O PDF é lido pela posição de cada célula, não pelo texto corrido: é o que faz uma etapa nova ou um equipamento inédito não desalinharem a leitura.'),
          h('p', { style: { margin: 0 } },
            'O que o leitor não entender vai para a quarentena, com o arquivo guardado e o motivo escrito. Nada entra pela metade.'),
        ]),
      );
    }
    if (qual === 'ajuda-triagem') {
      void avisarEmFolha(
        'O que é a triagem de estações',
        h('.vstack.gap-2', [
          h('p', { style: { margin: 0 } },
            'Cada etapa da OS traz um "Equip./Fornecedor". O sistema usa esse texto para saber em que máquina a etapa roda.'),
          h('p', { style: { margin: 0 } },
            'Quando aparece um texto que ninguém ensinou, a OS entra assim mesmo e a etapa espera na triagem. Você diz uma vez onde aquilo roda, e todas as etapas paradas com o mesmo texto entram na fila.'),
        ]),
      );
    }
  });
}

async function exportarCarteira(app: Aplicacao): Promise<void> {
  const linhas = await app.repo.carteira();
  const cabecalho = ['OS', 'Cliente', 'Quantidade', 'Produto', 'Entrega', 'Situação', 'Setor', 'Etapas', 'Falta (h:mm)'];
  const corpo = linhas.map((l) => [
    l.os.numero,
    l.os.clienteCurto,
    l.os.quantidade,
    l.os.produto,
    l.os.previsaoEntregaEm.slice(0, 10),
    l.atrasada ? 'Atrasada' : l.estado.status,
    l.setorAtual?.nome ?? '',
    `${l.estado.etapasConcluidas}/${l.estado.etapasTotal}`,
    `${Math.floor(l.estado.tempoPrevistoRestanteSeg / 3600)}:${String(Math.floor((l.estado.tempoPrevistoRestanteSeg % 3600) / 60)).padStart(2, '0')}`,
  ]);
  const csv = [cabecalho, ...corpo]
    .map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  // O BOM (\uFEFF) na frente não é sujeira: sem ele o Excel em português
  // abre o CSV como ANSI e todo acento vira símbolo. Escrito como escape
  // porque o caractere literal é invisível e some ao copiar e colar.
  const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `pcp-carteira-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  notificar({ titulo: 'Carteira exportada', corpo: `${linhas.length} OS`, icone: 'square-and-arrow-down' });
}

void iniciar().catch((erro) => {
  const raiz = document.getElementById('raiz');
  if (raiz) {
    render(
      raiz,
      telaDeAbertura({
        titulo: 'O PCP não conseguiu abrir',
        descricao: String(erro instanceof Error ? erro.message : erro),
        tom: 'aviso',
        carregando: false,
        extra: botao('Tentar de novo', () => location.reload(), { estilo: 'primary' }),
      }),
    );
  }
  console.error(erro);
});
